"use client";
import React, { useState } from "react";
import {
  Box,
  Center,
  Separator,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  Table,
} from "@chakra-ui/react";
import { Heading } from "@/elements";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";
import { PricingGroup } from "../../elements";
import { CONSUMER_GPU_CPU_DATA } from "./constants";

const PRICE_UNIT = {
  HOURLY: "hourly",
  MONTHLY: "monthly",
};

export const DesktopView = ({ data, frequency }) => (
  <Box>
    <Table.Root striped border="2px solid rgb(237,242,247)" width="100%">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader textAlign="center">CPU</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">Memory</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">Base Clock | Boost</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">Disk</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">GPU</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">
            {frequency === PRICE_UNIT.HOURLY ? "Hourly" : "Monthly"} Price (USD)
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {data.map((item, index) => (
          <Table.Row key={index} fontSize="16px">
            <Table.Cell textAlign="center">{item.cpu}</Table.Cell>
            <Table.Cell textAlign="center">{item.memory}</Table.Cell>
            <Table.Cell textAlign="center">{item.cockBoost}</Table.Cell>
            <Table.Cell textAlign="center">{item.disk}</Table.Cell>
            <Table.Cell textAlign="center">{item.gpu}</Table.Cell>
            <Table.Cell
              textAlign="center"
              fontWeight="700"
              fontSize="18px"
              color="primary.200"
            >
              {frequency === PRICE_UNIT.HOURLY
                ? `$${(item.monthlyPrice / 730).toFixed(2)}`
                : `$${item.monthlyPrice}`}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  </Box>
);

export const MobileView = ({ data, frequency }) => (
  <SimpleGrid columns={1} gap={6}>
    {data.map((item, index) => (
      <VStack
        key={index}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="md"
        gap={4}
        w="100%"
        p={4}
        bg="white"
      >
        <Text fontWeight="bold" fontSize="lg">
          {item.cpu}
        </Text>

        <Separator borderColor="gray.200" />

        <Stack gap={2} w="full">
          <Box>
            <Text as="span" fontSize="sm" color="text.100">
              Memory:{" "}
            </Text>
            <Text as="span" fontWeight="medium" fontSize="md">
              {item.memory}
            </Text>
          </Box>
          <Box>
            <Text as="span" fontSize="sm" color="text.100">
              Base Clock | Boost:{" "}
            </Text>
            <Text as="span" fontWeight="medium" fontSize="md">
              {item.cockBoost}
            </Text>
          </Box>
          <Box>
            <Text as="span" fontSize="sm" color="text.100">
              Disk:{" "}
            </Text>
            <Text as="span" fontWeight="medium" fontSize="md">
              {item.disk}
            </Text>
          </Box>
          <Box>
            <Text as="span" fontSize="sm" color="text.100">
              GPU:{" "}
            </Text>
            <Text as="span" fontWeight="medium" fontSize="md">
              {item.gpu}
            </Text>
          </Box>
          <Box>
            <Text as="span" fontSize="sm" color="text.100">
              {frequency === PRICE_UNIT.HOURLY ? "Hourly" : "Monthly"} Price:{" "}
            </Text>
            <Text as="span" fontWeight="bold" fontSize="lg" color="primary.200">
              {frequency === PRICE_UNIT.HOURLY
                ? `$${(item.monthlyPrice / 730).toFixed(2)}`
                : `$${item.monthlyPrice}`}
            </Text>
          </Box>
        </Stack>
      </VStack>
    ))}
  </SimpleGrid>
);

const ConsumerGpuTable = ({ showHeading = true }) => {
  const [frequency, setFrequency] = useState(PRICE_UNIT.HOURLY);
  const { title, data } = CONSUMER_GPU_CPU_DATA;
  const HeadingVariant = useHeadingVariant();

  return (
    <Box id="consumer-gpu">
      {showHeading && (
        <Heading variant={HeadingVariant} textAlign="center" mb={8}>
          {title}
        </Heading>
      )}

      <PricingGroup frequency={frequency} onChange={setFrequency} />

      <Center display={{ md: "none" }} w="full">
        <MobileView data={data} frequency={frequency} />
      </Center>
      <Box display={{ base: "none", md: "block" }}>
        <DesktopView data={data} frequency={frequency} />
      </Box>
    </Box>
  );
};

export default ConsumerGpuTable;
